<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names.ts'

defineProps({
  studentName: {
    type: String,
    required: true
  },
  gradeLabel: {
    type: String,
    required: true
  },
  showRestart: {
    type: Boolean,
    default: true
  }
})

const router = useRouter()
const showRestartModal = ref(false)
const analytics = getAnalyticsTracker()

function handleRestartClick() {
  analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.NEW_SESSION.RESTART)
  showRestartModal.value = true
}

function confirmRestart() {
  showRestartModal.value = false
  router.push({ name: 'PresentlevelsNewSession' })
}

function cancelRestart() {
  showRestartModal.value = false
}
</script>

<template>
  <section class="md:w-plw-subject-summary bg-gray-200 rounded-xs px-6 py-2 mb-3 text-md text-base-defaultText">
    <div class="flex items-center justify-between">
      <div class="flex flex-1">
        <div class="w-1/2">
          <span class="mr-2">STUDENT NAME:</span>
          <span data-testid="student-name" class="font-bold fs-exclude">{{ studentName }}</span>
        </div>
        <div class="w-1/2 ml-4">
          <span class="mr-2">ENROLLED GRADE:</span>
          <span data-testid="enrolled-grade" class="font-bold">{{ gradeLabel }}</span>
        </div>
      </div>
      <button 
        v-if="showRestart"
        type="button"
        @click="handleRestartClick"
        class="group text-blue-400 hover:text-blue-500 cursor-pointer ml-4 flex items-center gap-1"
        data-testid="restart-link"
      >
        <span class="material-icons-round text-lg" aria-hidden="true">replay</span>
        <span class="group-hover:underline">Restart</span>
      </button>
    </div>
  </section>

  <GbDialog
    dialog-id="restart-confirmation-modal"
    v-model:visible="showRestartModal"
    header="Restart Present Levels Wizard"
    :footer-buttons="true"
  >
    <template #bodyContent>
      <p class="text-base">
        If you restart the wizard all unsaved changes will be lost. Are you sure you want to restart?
      </p>
    </template>
    <template #footerButtonLeft>
      <GbButton
        @click="cancelRestart"
        gb-theme="grayOutlineSecondary"
        class="px-4 py-2"
      >
        Cancel
      </GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton
        @click="confirmRestart"
        gb-theme="bluePrimary"
        class="px-4 py-2"
        data-testid="confirm-restart-button"
      >
        <span class="flex items-center gap-1">
          <span class="material-icons-round text-1xl" aria-hidden="true">replay</span>
          <span>Restart</span>
        </span>
      </GbButton>
    </template>
  </GbDialog>
</template>
